import { HeaderGroup, flexRender } from '@tanstack/react-table';
import { Icon, IconArrowBigDown, IconArrowBigUp, IconExpand } from '@flixbus/honeycomb-icons-react';

import { ColumnResizeMode } from '@tanstack/table-core';
import React from 'react';
import clsx from 'clsx';
import styles from '../data-table.module.scss';

interface Props<TData> {
    headerGroup: HeaderGroup<TData>;
    columnResizeMode: ColumnResizeMode;
    columnDeltaOffset: number | null;
}

const DataTableHeadRow = <TData,>({ headerGroup, columnResizeMode, columnDeltaOffset }: Props<TData>) => (
    <tr key={headerGroup.id}>
        {headerGroup.headers.map((header) => {
            const { columnDef, getIsResizing, getCanResize, getCanSort, getIsSorted, getToggleSortingHandler } =
                header.column;

            const resizerClasses = clsx(styles.resizer, {
                [styles.resizing]: getIsResizing(),
            });

            const isSortable = getCanSort();
            const sortingClasses = clsx(styles.headerCell, {
                [styles.sortable]: isSortable,
            });

            const sortIcon =
                {
                    asc: IconArrowBigDown,
                    desc: IconArrowBigUp,
                }[getIsSorted() as string] ?? IconExpand;

            const width = columnDef.size !== -1 ? header.getSize() : undefined;

            return (
                <th scope="col" key={header.id} colSpan={header.colSpan} style={{ position: 'relative', width }}>
                    <div className={sortingClasses} onClick={getToggleSortingHandler()}>
                        {header.isPlaceholder ? null : flexRender(columnDef.header, header.getContext())}
                        {isSortable && <Icon InlineIcon={sortIcon} aria-hidden="true" extraClasses={styles.sortIcon} />}
                    </div>
                    {getCanResize() && (
                        <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={resizerClasses}
                            style={{
                                transform:
                                    columnResizeMode === 'onEnd' && getIsResizing()
                                        ? `translateX(${columnDeltaOffset}px)`
                                        : '',
                            }}
                        />
                    )}
                </th>
            );
        })}
    </tr>
);

export default DataTableHeadRow;
